import React, { useState, useEffect } from 'react';
import { contentAPI } from '../services/api';
import LoadingScreen from './LoadingScreen';
import { WarningIcon } from './Icons';
import styles from '../styles/DetailView.module.css';

/**
 * News Detail View Component
 * Displays full news item content with markdown rendering
 *
 * Props:
 * - newsId: News item ID to load
 * - onBack: Callback when back button is clicked
 */
function NewsDetailView({ newsId, onBack }) {
  const [news, setNews] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadNews() {
      if (!newsId) {
        setError('No news item specified');
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const data = await contentAPI.getNewsItem(newsId);
        setNews(data);
      } catch (err) {
        console.error('[NewsDetailView] Error loading news:', err);
        setError(err.message || 'Failed to load news item');
      } finally {
        setIsLoading(false);
      }
    }

    loadNews();
  }, [newsId]);

  // Format date to relative time
  const formatDate = (dateString) => {
    if (!dateString) return null;

    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffTime = Math.abs(now - date);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return date.toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  // Extract domain from source URL
  const getSourceDomain = (url) => {
    if (!url) return '';
    try {
      return new URL(url).hostname.replace('www.', '');
    } catch {
      return '';
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.detailView}>
        <div className={styles.detailHeader}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
        </div>
        <LoadingScreen text="Loading news..." variant="dots" showImage={true} />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={styles.detailView}>
        <div className={styles.detailHeader}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>
            <WarningIcon size={64} />
          </div>
          <div className={styles.errorText}>{error}</div>
          <button onClick={onBack} className={styles.errorButton}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // News not found
  if (!news) {
    return (
      <div className={styles.detailView}>
        <div className={styles.detailHeader}>
          <button onClick={onBack} className={styles.backButton}>
            ← Back
          </button>
        </div>
        <div className={styles.errorContainer}>
          <div className={styles.errorIcon}>
            <WarningIcon size={64} />
          </div>
          <div className={styles.errorText}>News item not found</div>
          <button onClick={onBack} className={styles.errorButton}>
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const sourceDomain = getSourceDomain(news.sourceUrl);
  const displaySource = sourceDomain;
  const publishDate = formatDate(news.publishedDate);

  return (
    <div className={styles.detailView}>
      <div className={styles.detailHeader}>
        <button onClick={onBack} className={styles.backButton}>
          ← Back
        </button>
        <div className={styles.categoryBadge}>
          News
        </div>
      </div>

      <div className={styles.detailContent}>
        <h1 className={styles.detailTitle}>{news.headline}</h1>

        <div className={styles.newsMetaBar}>
          {displaySource && (
            <div className={styles.newsSource}>
              {displaySource}
            </div>
          )}
          {publishDate && (
            <div className={styles.newsDate}>
              {publishDate}
            </div>
          )}
        </div>

        {news.summary && (
          <div className={styles.detailDescription}>{news.summary}</div>
        )}

        {news.heroImage && (
          <div className={styles.detailImageContainer}>
            <img src={news.heroImage} alt={news.headline} className={styles.detailImage} />
          </div>
        )}

        {news.sourceUrl && (
          <div className={styles.externalLinkContainer}>
            <a
              href={news.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.externalLink}
            >
              Read full article on {displaySource || 'source'} →
            </a>
          </div>
        )}

        <div className={styles.bottomBackButton}>
          <button onClick={onBack}>
            Back to News
          </button>
        </div>
      </div>
    </div>
  );
}

export default NewsDetailView;
